import { useState, useEffect } from "react";
import Swipeable from "@/components/Swipeable";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Home, Loader2, Star, Check, X, Trash2, Video, Quote,
  Link2, Copy, Share2, MessageCircle, ShieldCheck, Clock, CheckCircle2, XCircle
} from "lucide-react";
import { authGet, authPut, authDelete, isAdvisor } from "../utils/auth";
import MobileBottomNav from "../components/MobileBottomNav";
import MobileSidebar from "../components/MobileSidebar";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "pending", label: "Pending", icon: Clock, color: "#A87608" },
  { id: "approved", label: "Approved", icon: CheckCircle2, color: "#10b981" },
  { id: "rejected", label: "Rejected", icon: XCircle, color: "#ef4444" },
  { id: "all", label: "All", icon: Video, color: "#8ea2c9" },
];

const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`h-4 w-4 ${i <= n ? "text-[#A87608] fill-[#D4A017]" : "text-[#3a4a6d]"}`} />
    ))}
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: "Pending", cls: "bg-[#D4A017]/15 text-[#A87608]" },
    approved: { label: "Live on site", cls: "bg-emerald-500/15 text-emerald-600" },
    rejected: { label: "Rejected", cls: "bg-red-500/15 text-red-600" },
  };
  const s = map[status] || map.pending;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.cls}`}>{s.label}</span>;
};

export default function TestimonialsAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [tab, setTab] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);

  const shareLink = `${window.location.origin}/testimonial`;

  useEffect(() => {
    if (!isAdvisor()) { navigate("/advisor/dashboard"); return; }
    fetchData(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const fetchData = async (status) => {
    setLoading(true);
    try {
      const q = status && status !== "all" ? `?status=${status}` : "";
      const res = await authGet(`/api/advisor/testimonials${q}`);
      if (res.ok) {
        setItems(res.data.testimonials || []);
        setCounts(res.data.counts || counts);
      }
    } catch (e) { /* noop */ }
    setLoading(false);
  };

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      const res = await authPut(`/api/advisor/testimonials/${id}`, { status });
      if (res.ok) {
        toast.success(status === "approved" ? "Approved — now live on your website" : status === "rejected" ? "Rejected" : "Updated");
        fetchData(tab);
      } else toast.error(res.data?.detail || "Failed to update");
    } catch { toast.error("Network error"); }
    setBusyId(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial permanently?")) return;
    setBusyId(id);
    try {
      const res = await authDelete(`/api/advisor/testimonials/${id}`);
      if (res.ok) { toast.success("Deleted"); fetchData(tab); }
      else toast.error("Failed to delete");
    } catch { toast.error("Network error"); }
    setBusyId(null);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success("Link copied! Share it with your clients.");
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`Hi! It was great working with you. Could you share a quick testimonial about your experience? It really helps. 🙏\n\n${shareLink}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#F4F6FA] text-[#1B2A4A] pb-24 md:pb-8">
      <MobileSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />
      <header className="sticky top-0 z-40 bg-[#F4F6FA]/95 backdrop-blur-sm border-b border-[#C9D4E6]">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-[#EDF1F8] rounded-lg"><ArrowLeft className="h-5 w-5" /></button>
          <button onClick={() => navigate("/advisor/dashboard")} className="p-2 hover:bg-[#EDF1F8] rounded-lg"><Home className="h-5 w-5" /></button>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
              <Quote className="h-5 w-5 text-[#0a1628]" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Customer Testimonials</h1>
              <p className="text-xs text-[#3A4557]">Collect, approve & showcase</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-5">
        {/* Shareable link card */}
        <div className="bg-gradient-to-r from-[#EDF1F8]/60 to-[#EDF1F8] border border-[#D4A017]/30 rounded-2xl p-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="h-5 w-5 text-[#A87608]" />
            <h2 className="font-semibold">Your testimonial collection link</h2>
          </div>
          <p className="text-sm text-[#33436B] mb-3">Share this branded link with clients on WhatsApp. They can record a video or write a testimonial — no login needed.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex items-center gap-2 bg-[#F4F6FA] border border-[#C9D4E6] rounded-xl px-3 py-2.5 min-w-0">
              <ShieldCheck className="h-4 w-4 text-[#A87608] shrink-0" />
              <span className="text-sm text-[#2E3B57] truncate" data-testid="testimonial-share-link">{shareLink}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={copyLink} data-testid="copy-link-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#EDF1F8] hover:bg-[#E2E9F4] text-sm font-medium">
                <Copy className="h-4 w-4" /> Copy
              </button>
              <button onClick={shareWhatsApp} data-testid="share-whatsapp-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-medium">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Swipeable outerClassName="mb-5" className="flex gap-2 pb-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            const count = t.id === "all" ? counts.total : counts[t.id];
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors border"
                style={{
                  backgroundColor: active ? t.color : "transparent",
                  color: active ? "#0a1628" : t.color,
                  borderColor: active ? t.color : "#C9D4E6",
                }}
              >
                <Icon className="h-4 w-4" />
                {t.label}
                <span className={`px-1.5 rounded-full text-xs ${active ? "bg-black/15" : "bg-[#EDF1F8]"}`}>{count ?? 0}</span>
              </button>
            );
          })}
        </Swipeable>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#A87608]" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-white/40 border border-[#C9D4E6] rounded-2xl">
            <Quote className="h-12 w-12 text-[#3a4a6d] mx-auto mb-3" />
            <p className="text-[#33436B] font-medium">No {tab !== "all" ? tab : ""} testimonials yet</p>
            <p className="text-[#33436B] text-sm mt-1">Share your link above to start collecting.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="testimonials-list">
            {items.map((t) => (
              <div key={t.id} className="bg-white/50 border border-[#C9D4E6] rounded-2xl overflow-hidden flex flex-col" data-testid={`testimonial-card-${t.id}`}>
                {t.has_video && (
                  <video src={`${API}${t.video_url}`} controls playsInline className="w-full max-h-64 bg-black" data-testid={`testimonial-video-${t.id}`} />
                )}
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="h-9 w-9 rounded-full bg-[#EDF1F8] flex items-center justify-center shrink-0">
                        <span className="font-bold text-[#A87608]">{t.customer_name?.charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{t.customer_name}</p>
                        <p className="text-xs text-[#3A4557] truncate">{t.customer_city || "—"}</p>
                      </div>
                    </div>
                    <StatusBadge status={t.status} />
                  </div>
                  <Stars n={t.rating} />
                  {t.message && <p className="text-sm text-[#2E3B57] mt-2 whitespace-pre-wrap flex-1">{t.message}</p>}

                  <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-[#C9D4E6]">
                    {t.status !== "approved" && (
                      <button onClick={() => setStatus(t.id, "approved")} disabled={busyId === t.id} data-testid={`approve-${t.id}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium">
                        {busyId === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Approve
                      </button>
                    )}
                    {t.status !== "rejected" && (
                      <button onClick={() => setStatus(t.id, "rejected")} disabled={busyId === t.id} data-testid={`reject-${t.id}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#EDF1F8] hover:bg-[#E2E9F4] text-[#2E3B57] text-xs font-medium">
                        <X className="h-3.5 w-3.5" /> {t.status === "approved" ? "Unpublish" : "Reject"}
                      </button>
                    )}
                    <button onClick={() => remove(t.id)} disabled={busyId === t.id} data-testid={`delete-${t.id}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-600 text-xs font-medium ml-auto">
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <MobileBottomNav onMenuClick={() => setShowSidebar(true)} />
    </div>
  );
}
